import { inject, Injectable } from '@angular/core';
import { HostnameService } from '@core/services/hostname.service';
import { AppRoutes } from '@core/enums/routes.enum';
import { QUESTIONNAIRE_LOCALE_PATH_INDEX } from '../tokens/questionnaire-tenant.token';

export enum Locale {
  'EN' = 'en',
  'EL' = 'el',
  'ES' = 'es',
  'NL' = 'nl',
  'PL' = 'pl',
  'DE' = 'de',
  'IT' = 'it',
  'GR' = 'gr',
}

export const LanguageLocaleMap: Record<Locale, string> = {
  [Locale.EN]: 'en-US',
  [Locale.EL]: 'el-GR',
  [Locale.ES]: 'es-ES',
  [Locale.NL]: 'nl-BE',
  [Locale.PL]: 'pl-LP',
  [Locale.DE]: 'de-DE',
  [Locale.IT]: 'it-It',
  [Locale.GR]: 'el-GR',
};

@Injectable({
  providedIn: 'root',
})
export class PublicLocaleService {
  private readonly localePathIndex = inject(QUESTIONNAIRE_LOCALE_PATH_INDEX, { optional: true });

  constructor(private hostnameService: HostnameService) {}

  public get locale(): Locale {
    const pathName = this.hostnameService.pathName;
    const isLanding = pathName === `/${AppRoutes.projectInformation}`;
    const isAuth = pathName.startsWith('/auth/') || pathName === '/auth';

    const pathIndex = this.localePathIndex ?? 1;
    const langFromUrl = pathName.split('/')[pathIndex]?.toLowerCase();

    if (langFromUrl === Locale.GR) {
      return Locale.EL;
    }

    if (!isLanding) {
      // Resolution order:
      // 1. ?locale= query param (highest — explicit URL override).
      // 2. URL path segment locale (questionnaire-style /:locale/...).
      // 3. Cached user's DB locale (returning users — survives a 401
      //    because the user record is separate from the auth token).
      // 4. Browser/OS language via navigator.language — login flow
      //    only, per LGE-1555. Questionnaire keeps NL-first behaviour.
      // 5. Cold default: EN for landing + login (LGE-1555),
      //    NL for the questionnaire and other public flows.
      const fromQuery = this.matchLocale(this.readQueryLocale());

      if (fromQuery) {
        return fromQuery;
      }

      const fromUrl = this.matchLocale(langFromUrl);

      if (fromUrl) {
        return fromUrl;
      }

      const fromCachedUser = this.matchLocale(this.readCachedUserLocale());

      if (fromCachedUser) {
        return fromCachedUser;
      }

      if (isAuth) {
        const fromBrowser = this.readBrowserLocale();

        if (fromBrowser) {
          return fromBrowser;
        }
      }
    }

    return isLanding || isAuth ? Locale.EN : Locale.NL;
  }

  public get fullLocale(): string {
    return LanguageLocaleMap[this.locale];
  }

  private readQueryLocale(): string | null {
    return new URLSearchParams(this.hostnameService.search ?? '').get('locale');
  }

  private readCachedUserLocale(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    try {
      const raw = localStorage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;

      return user?.locale?.code ?? null;
    } catch {
      return null;
    }
  }

  private readBrowserLocale(): Locale | null {
    const navigatorLanguage = typeof navigator === 'undefined' ? null : navigator.language;
    const primaryTag = navigatorLanguage?.split('-')[0]?.toLowerCase();

    return this.matchLocale(primaryTag);
  }

  private matchLocale(candidate: string | null | undefined): Locale | null {
    if (!candidate) {
      return null;
    }

    const found = Object.values(Locale).find((loc) => {
      return loc === candidate;
    });

    return found ?? null;
  }
}
